import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Select } from 'antd';
import WorkflowTag from '../../forms/FormWorkflows/WorkflowTag';

// Match EmailRecipientSelector validation for manually entered addresses
// eslint-disable-next-line no-useless-escape
const emailRegex = /^[^@\s]+@[^@\s\.]+(?:\.[^@\.\s]+)+$/;

const tokenSeparators = [',', '\n'];

export default function ExternalRecipientsSelector({
  isDisplay,
  value,
  onChange,
  recipientOptions,
  reminderValue,
}) {
  const allowedIds = useMemo(
    () => new Set(recipientOptions.map((o) => o.value)),
    [recipientOptions],
  );

  const recipientLabelMap = useMemo(() => {
    const map = {};
    recipientOptions.forEach(({label, value}) => {
      map[value] = label;
    });
    return map;
  }, [recipientOptions]);

  const handleChange = useCallback((next) => {
    const filtered = next.filter(
      (entry) => allowedIds.has(entry) || emailRegex.test(entry),
    );
    onChange(filtered);
  }, [allowedIds, onChange]);

  const disabled = isDisplay || (!reminderValue && reminderValue !== 0);

  const tagRender = useCallback((tagProps) => {
    const { label, value } = tagProps;
    let labelText = '';
    if (recipientLabelMap[value]) {
      labelText = recipientLabelMap[value];
    } else if (typeof label === 'string') {
      labelText = label;
    } else {
      labelText = String(value ?? '');
    }
    return (
      <WorkflowTag
        closable={!isDisplay}
        itemProps={tagProps}
        label={labelText}
        type="email"
      />
    );
  }, [isDisplay, recipientLabelMap]);

  return (
    <Select
      mode="tags"
      tokenSeparators={tokenSeparators}
      style={{ width: '100%', margin: '10px 0' }}
      placeholder="Recipients"
      showSearch
      optionFilterProp="label"
      optionLabelProp="label"
      dropdownMatchSelectWidth={false}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      tagRender={tagRender}
    >
      {recipientOptions.map(({value, label, children}) => (
        <Select.Option
          key={value}
          value={value}
          label={label}
        >
          {children || label}
        </Select.Option>
      ))}
    </Select>
  );
}

ExternalRecipientsSelector.propTypes = {
  isDisplay: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    children: PropTypes.node,
  })),
  reminderValue: PropTypes.number,
  value: PropTypes.arrayOf(PropTypes.string),
};

ExternalRecipientsSelector.defaultProps = {
  isDisplay: false,
  recipientOptions: [],
  reminderValue: undefined,
  value: [],
};
